import moment from 'moment';
import { dateOnlyToUnix, localTodayDateOnlyUnix } from './dateOnly';

/** Project start unless installation range is active; then installation window start. */
export function getBaseUnixForLeadTime(values, projectStartUnixOverride) {
  const projectUnix =
    projectStartUnixOverride ?? values?.start ?? localTodayDateOnlyUnix();
  if (
    (values.installation_start_date || values.installation_end_date) &&
    values.installation_start_date
  ) {
    return values.installation_start_date;
  }
  return projectUnix;
}

/**
 * Max lead_time (days) among products that have lead_time > 0.
 * Returns null if none.
 */
export function getMaxCatalogLeadDays(products) {
  const withLead = (products || []).filter((p) => p?.lead_time > 0);
  if (!withLead.length) return null;
  return Math.max(...withLead.map((p) => p.lead_time));
}

/** Fill missing lead_time from catalog list (order lines often omit it). */
export function resolveProductsLeadTime(products, catalog) {
  const list = catalog || [];
  return (products || []).map((p) => {
    if (Number(p?.lead_time) > 0) return p;
    const productId = p?.product_id ?? p?.id;
    if (productId == null || productId === '') return p;
    const match = list.find((c) => Number(c?.id) === Number(productId));
    if (Number(match?.lead_time) > 0) {
      return { ...p, lead_time: match.lead_time };
    }
    return p;
  });
}

/**
 * Required install-end unix = base + max catalog lead days.
 * Returns null if no products have lead_time.
 */
export function getRequiredInstallEndUnix(values, baseUnix, products) {
  const highestLeadTime = getMaxCatalogLeadDays(products);
  if (highestLeadTime == null) return null;
  return moment.unix(baseUnix).utc().add(highestLeadTime, 'days').unix();
}

/**
 * True when a preset install end exists and is earlier than the required lead-time end.
 * Used to auto-ON + lock Rush Fee (user may still pick an earlier date manually).
 */
export function isInstallBeforeRequiredLead(values, products, projectStartUnixOverride) {
  const baseUnix = getBaseUnixForLeadTime(values, projectStartUnixOverride);
  const requiredUnix = getRequiredInstallEndUnix(values, baseUnix, products);
  if (requiredUnix == null) return false;
  const useRange = values.installation_start_date || values.installation_end_date;
  const presetUnix = useRange ? values?.installation_end_date : values?.end;
  if (presetUnix == null || presetUnix === '') return false;
  // Compare calendar days — same-day install vs required lead is not "before"
  return moment
    .unix(Number(presetUnix))
    .utc()
    .startOf('day')
    .isBefore(moment.unix(Number(requiredUnix)).utc().startOf('day'));
}

/**
 * From base date + max product lead days, compute { field, finalUnix } for Formik
 * (single `end` vs range `installation_end_date`).
 * Auto-adjusts install end forward to at least the required lead-time date
 * when products/start change. User can still manually pick an earlier date
 * in the picker; that is not overwritten until the next lead-time patch.
 * Returns null if no products have lead_time.
 */
export function getInstallEndPatchFromLead(values, baseUnix, products) {
  const requiredUnix = getRequiredInstallEndUnix(values, baseUnix, products);
  if (requiredUnix == null) return null;
  const useRange = values.installation_start_date || values.installation_end_date;
  const field = useRange ? 'installation_end_date' : 'end';
  const presetUnix = useRange ? values?.installation_end_date : values?.end;
  const finalUnix =
    presetUnix != null && presetUnix !== ''
      ? Math.max(Number(requiredUnix), Number(presetUnix))
      : requiredUnix;
  return { field, finalUnix };
}
